import * as React from "react"
import { useState, useEffect, useRef } from "react"
import { cn } from "~/lib/utils"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { Input } from "~/react/ui/input"
import { Hint } from "~/react/ui/hint"
import { Label } from "~/react/ui/label"
import SignatureEditor from "~/react/components/signature_editor"
import { validateManualAccount } from "~/react/components/email_accounts/validation"
import { CurrencyHelper } from "~/helpers/currency_helper"

type AccountData = {
  first: string
  last: string
  local: string
  domain: string
  signature?: string
}

type EmailAccountCardProps = {
  id: string
  summary?: React.ReactNode
  price?: string
  open?: boolean
  defaultOpen?: boolean
  invalid?: boolean
  className?: string
  summaryClassName?: string
  actionsClassName?: string
  bodyClassName?: string
  onOpenChange?: (open: boolean) => void
  onRemove?: () => void
  showDeleteButton?: boolean
  // Validation props - if provided, card will validate itself
  account?: AccountData
  allowedDomains?: string[]
  globalSignature?: string
  // Form mode props - if provided, card will render form fields
  mode?: "edit" | "preview"
  onChange?: (updated: AccountData) => void
  duplicate?: boolean
  hasGlobalSignature?: boolean
  externalErrors?: string[]
  idPrefix?: string
  // Currency props for price formatting
  currencyCode?: string
  emailPriceCents?: number
  children?: React.ReactNode
}

type EmailAccountCardContentProps = {
  className?: string
  forceMount?: boolean
  children: React.ReactNode
}

type EmailAccountCardContextValue = {
  open: boolean
  contentId: string
  validationErrors?: string[]
}

const EmailAccountCardContext =
  React.createContext<EmailAccountCardContextValue | null>(null)

export function useEmailAccountCardContext() {
  return React.useContext(EmailAccountCardContext)
}

export function EmailAccountCardContent({
  className,
  forceMount,
  children,
}: EmailAccountCardContentProps) {
  const ctx = React.useContext(EmailAccountCardContext)
  const isOpen = ctx?.open ?? false
  if (!isOpen && !forceMount) return null
  return (
    <div
      id={ctx?.contentId}
      data-state={isOpen ? "open" : "closed"}
      className={cn("email-account-card__body", className)}
      hidden={!isOpen && !!forceMount}
    >
      {children}
    </div>
  )
}

export function EmailAccountCard({
  id,
  summary,
  price,
  open,
  defaultOpen,
  invalid: invalidProp = false,
  className,
  summaryClassName,
  actionsClassName,
  bodyClassName,
  onOpenChange,
  onRemove,
  showDeleteButton = true,
  account,
  allowedDomains = [],
  globalSignature,
  mode,
  onChange,
  duplicate = false,
  hasGlobalSignature = false,
  externalErrors = [],
  idPrefix,
  currencyCode,
  emailPriceCents,
  children,
}: EmailAccountCardProps) {
  const hasExplicitContent = React.Children.toArray(children).some(
    (child) =>
      React.isValidElement(child) && child.type === EmailAccountCardContent,
  )

  const isControlled = typeof open === "boolean"
  const [internalOpen, setInternalOpen] = React.useState<boolean>(!!defaultOpen)
  const isOpen = isControlled ? !!open : internalOpen
  const contentId = `email-account-card-${id}-content`

  // Form field state (only for edit mode)
  const prefix =
    idPrefix ??
    (mode === "preview" ? `email-accounts-preview-${id}` : `manual-${id}`)
  const [blurredFields, setBlurredFields] = useState<Set<string>>(new Set())
  const [focusedFields, setFocusedFields] = useState<Set<string>>(new Set())
  const signatureEditorRef = useRef<HTMLDivElement>(null)

  // Handle focus/blur for signature editor (Trix editor) - only for edit mode
  useEffect(() => {
    if (mode !== "edit") return
    const editorElement = document.getElementById(`${prefix}-signature-editor`)
    if (!editorElement) return

    const handleSignatureFocus = () => {
      setFocusedFields((prev) => new Set(prev).add("signature"))
    }

    const handleSignatureBlur = () => {
      setBlurredFields((prev) => new Set(prev).add("signature"))
      setFocusedFields((prev) => {
        const next = new Set(prev)
        next.delete("signature")
        return next
      })
    }

    editorElement.addEventListener("focus", handleSignatureFocus, true)
    editorElement.addEventListener("blur", handleSignatureBlur, true)
    return () => {
      editorElement.removeEventListener("focus", handleSignatureFocus, true)
      editorElement.removeEventListener("blur", handleSignatureBlur, true)
    }
  }, [id, mode, prefix])

  const handleBlur = (fieldName: string) => {
    if (mode === "edit") {
      setBlurredFields((prev) => new Set(prev).add(fieldName))
      setFocusedFields((prev) => {
        const next = new Set(prev)
        next.delete(fieldName)
        return next
      })
    }
  }

  const handleFocus = (fieldName: string) => {
    if (mode === "edit") {
      setFocusedFields((prev) => new Set(prev).add(fieldName))
    }
  }

  const handleFieldChange = (updates: Partial<AccountData>) => {
    if (onChange && account) {
      onChange({ ...account, ...updates })
    }
  }

  // Internal validation - compute invalid state and errors if account data is provided
  const validationResult = React.useMemo(() => {
    if (invalidProp)
      return {
        isValid: false,
        errors: externalErrors.length > 0 ? externalErrors : [],
      } // Explicit invalid prop takes precedence
    if (!account) return { isValid: true, errors: [] } // No account data, no validation

    // Use external errors if provided, otherwise validate
    if (externalErrors.length > 0) {
      return { isValid: false, errors: externalErrors }
    }

    // Check if account has any data (not completely blank)
    const hasData =
      (account.first || "").trim() ||
      (account.last || "").trim() ||
      (account.local || "").trim() ||
      (account.domain || "").trim() ||
      (account.signature || "").trim()

    // If completely blank, don't mark as invalid
    if (!hasData) return { isValid: true, errors: [] }

    // Validate the account
    const validation = validateManualAccount(
      {
        id,
        first: account.first || "",
        last: account.last || "",
        local: account.local || "",
        domain: account.domain || "",
        signature: account.signature || "",
      },
      {
        allowedDomains: allowedDomains.map((d) => (d || "").toLowerCase()),
        allowGlobalSignature: true,
        globalSignature: globalSignature || "",
      },
    )

    return { isValid: validation.isValid, errors: validation.errors }
  }, [
    invalidProp,
    account,
    allowedDomains,
    globalSignature,
    id,
    externalErrors,
  ])

  const computedInvalid = !validationResult.isValid

  // Compute summary text if not provided
  const computedSummary =
    summary ??
    (account
      ? account.first || account.last || account.local
        ? `${account.local || i18n.t("js.email_accounts.accounts.untitled_local")}@${account.domain || i18n.t("js.email_accounts.accounts.domain_placeholder")}`
        : i18n.t("js.email_accounts.accounts.untitled_email_account")
      : "")

  // Compute price if not provided - format in user's currency
  const computedPrice = React.useMemo(() => {
    if (price) return price
    if (account?.local && account?.domain && emailPriceCents && currencyCode) {
      const currencySymbol = CurrencyHelper.currencySymbol(currencyCode)
      return CurrencyHelper.numberToCurrency(
        emailPriceCents / 100,
        currencySymbol,
      )
    }
    return undefined
  }, [price, account?.local, account?.domain, emailPriceCents, currencyCode])

  // Compute open state for edit mode
  const computedOpenForEdit =
    mode === "edit" && account
      ? !account.local && !account.domain && open !== false
        ? true
        : open
      : undefined
  const finalOpen =
    computedOpenForEdit !== undefined ? computedOpenForEdit : isOpen

  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next)
    onOpenChange?.(next)
  }

  const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault()
    setOpen(!finalOpen)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === " " || e.key === "Enter") {
      handleToggle(e)
    }
  }

  const containerClasses = cn(
    "email-account-card",
    computedInvalid && "email-account-card--invalid",
    className,
  )

  const summaryContent = (
    <span className="d-flex align-items-center justify-content-between w-100">
      {typeof computedSummary === "string" ? (
        <span>{computedSummary}</span>
      ) : (
        computedSummary
      )}
      {computedPrice && (
        <span className="text-xs ml-auto">
          {computedPrice}
          <span className="text-body-secondary text-2xs">/month</span>
        </span>
      )}
    </span>
  )

  // Form field validation display logic
  const errors = validationResult.errors || []
  const missingFirst = errors.includes("first_required")
  const missingLast = errors.includes("last_required")
  const missingLocal = errors.includes("local_required")
  const missingDomain = errors.includes("domain_required")
  const missingSignature = errors.includes("signature_required")

  const hasAnyFieldFocused = focusedFields.size > 0
  const showFirstValidation =
    mode === "edit"
      ? !hasAnyFieldFocused && blurredFields.has("first") && missingFirst
      : missingFirst
  const showLastValidation =
    mode === "edit"
      ? !hasAnyFieldFocused && blurredFields.has("last") && missingLast
      : missingLast
  const showLocalValidation =
    mode === "edit"
      ? duplicate ||
        (!hasAnyFieldFocused &&
          blurredFields.has("local") &&
          (missingLocal || duplicate))
      : missingLocal || duplicate
  const showDomainValidation =
    mode === "edit"
      ? duplicate ||
        (!hasAnyFieldFocused &&
          blurredFields.has("domain") &&
          (missingDomain || duplicate))
      : missingDomain || duplicate
  const showSignatureValidation =
    mode === "edit"
      ? !hasAnyFieldFocused &&
        blurredFields.has("signature") &&
        missingSignature
      : missingSignature

  // Render form fields if mode is provided
  const renderFormFields = () => {
    if (!mode || !account) return null

    return (
      <>
        <div className="row g-4 mb-4">
          <div className="col-6">
            <label className="h-label" htmlFor={`${prefix}-first-name`}>
              {i18n.t("js.email_accounts.accounts.first_name")}
            </label>
            <Input
              fullWidth
              id={`${prefix}-first-name`}
              value={account.first}
              onChange={(e) => handleFieldChange({ first: e.target.value })}
              onFocus={() => handleFocus("first")}
              onBlur={() => handleBlur("first")}
              aria-invalid={showFirstValidation ? "true" : undefined}
            />
            {showFirstValidation && (
              <Hint variant="error" className="mt-1">
                {i18n.t(
                  "js.email_accounts.provisioning.accounts.first_required",
                )}
              </Hint>
            )}
          </div>
          <div className="col-6">
            <label className="h-label" htmlFor={`${prefix}-last-name`}>
              {i18n.t("js.email_accounts.accounts.last_name")}
            </label>
            <Input
              fullWidth
              id={`${prefix}-last-name`}
              value={account.last}
              onChange={(e) => handleFieldChange({ last: e.target.value })}
              onFocus={() => handleFocus("last")}
              onBlur={() => handleBlur("last")}
              aria-invalid={showLastValidation ? "true" : undefined}
            />
            {showLastValidation && (
              <Hint variant="error" className="mt-1">
                {i18n.t(
                  "js.email_accounts.provisioning.accounts.last_required",
                )}
              </Hint>
            )}
          </div>
        </div>
        <div className="row g-4 mb-4">
          <div className="col-6">
            <label className="h-label" htmlFor={`${prefix}-email`}>
              {i18n.t("js.email_accounts.accounts.email")}
            </label>
            <Input
              fullWidth
              id={`${prefix}-email`}
              value={account.local}
              onChange={(e) => handleFieldChange({ local: e.target.value })}
              onFocus={() => handleFocus("local")}
              onBlur={() => handleBlur("local")}
              aria-invalid={showLocalValidation ? "true" : undefined}
            />
            {showLocalValidation && (
              <Hint variant="error" className="mt-1">
                {duplicate
                  ? i18n.t("js.email_accounts.accounts.duplicate_email")
                  : i18n.t(
                      "js.email_accounts.provisioning.accounts.local_required",
                    )}
              </Hint>
            )}
          </div>
          <div className="col-6">
            <label className="h-label" htmlFor={`${prefix}-domain`}>
              {i18n.t("js.email_accounts.accounts.domain")}
            </label>
            <select
              className="h-select h-select--block"
              id={`${prefix}-domain`}
              value={account.domain}
              onChange={
                mode === "edit"
                  ? (e) => handleFieldChange({ domain: e.target.value })
                  : undefined
              }
              onFocus={
                mode === "edit" ? () => handleFocus("domain") : undefined
              }
              onBlur={mode === "edit" ? () => handleBlur("domain") : undefined}
              aria-invalid={showDomainValidation ? "true" : undefined}
            >
              <option value="" disabled>
                {allowedDomains.length === 0
                  ? i18n.t("js.email_accounts.accounts.no_domains_selected")
                  : i18n.t("js.email_accounts.accounts.select_a_domain")}
              </option>
              {allowedDomains.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            {showDomainValidation && !showLocalValidation && (
              <Hint variant="error" className="mt-1">
                {duplicate
                  ? i18n.t("js.email_accounts.accounts.duplicate_email")
                  : i18n.t(
                      "js.email_accounts.provisioning.accounts.domain_required",
                    )}
              </Hint>
            )}
          </div>
        </div>

        <Label htmlFor={`${prefix}-signature`}>
          {i18n.t("js.email_accounts.accounts.signature")}
        </Label>
        <div
          ref={signatureEditorRef}
          aria-invalid={showSignatureValidation ? "true" : undefined}
        >
          <SignatureEditor
            className={mode === "preview" ? "mb-0" : undefined}
            id={`${prefix}-signature-editor`}
            value={account.signature || ""}
            onChange={(html) => handleFieldChange({ signature: html })}
            previewSample={{ firstName: account.first, lastName: account.last }}
          />
        </div>
        {showSignatureValidation && (
          <Hint variant="error" className="mt-1">
            {hasGlobalSignature
              ? i18n.t(
                  "js.email_accounts.accounts.signature_missing_but_global",
                )
              : i18n.t(
                  "js.email_accounts.provisioning.accounts.signature_required",
                )}
          </Hint>
        )}
      </>
    )
  }

  return (
    <section
      className={containerClasses}
      data-state={finalOpen ? "open" : "closed"}
    >
      <header className={cn("email-account-card__header", summaryClassName)}>
        <button
          type="button"
          className="email-account-card__title text-xs-medium"
          aria-expanded={finalOpen}
          aria-controls={contentId}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
        >
          {summaryContent}
        </button>
        <div className={cn("email-account-card__actions", actionsClassName)}>
          {showDeleteButton && onRemove && (
            <Button
              variant="ghost"
              size="xs"
              iconOnly
              aria-label={i18n.t(
                "js.email_accounts.accounts.remove_email_account",
              )}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onRemove()
              }}
            >
              <Icon icon="trash" aria-hidden="true" size="xs" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="xs"
            iconOnly
            aria-label="Toggle"
            onClick={handleToggle}
            className="email-account-card__toggle"
          >
            <Icon icon="chevron-down" aria-hidden="true" size="xs" />
          </Button>
        </div>
      </header>
      <EmailAccountCardContext.Provider
        value={{
          open: finalOpen,
          contentId,
          validationErrors: validationResult.errors,
        }}
      >
        {hasExplicitContent ? (
          children
        ) : (
          <EmailAccountCardContent className={cn(bodyClassName)}>
            {mode ? renderFormFields() : children}
          </EmailAccountCardContent>
        )}
      </EmailAccountCardContext.Provider>
    </section>
  )
}
